import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { Fragment, useState } from 'react'
import {
  hardwareEventsPageQuery,
  hardwareEventsTableQuery,
  initialEventsQuery,
  type HardwareCategory,
  type HardwareEventRow,
} from '../api/devices'
import { groupInitialEvents } from './initialEvents'
import { EventsChrome, type EventsFilter } from '../ui/EventsChrome'
import { LENGTHS, pageButtons } from '../ui/DataTable'
import { Spinner } from '../ui/Spinner'
import type { ScreenProps } from './screens'

/**
 * Fila Hardware Changes - inventoree/devices/others/events.php cu bara
 * events_chrome.php si coloanele InventoreeDeviceEventsModel: data, mesaj,
 * user, authorised. Se deschide pe All (DEFAULT_CATEGORY = ''). Cautarea e
 * a barii, nu a DataTables din antetul paginii.
 */

const TABLE = 'deviceEvents'
const EMPTY_FILTER: EventsFilter = { category: '', q: '', from: '', to: '' }

export function DeviceHardwareEvents({ id }: ScreenProps) {
  const page = useQuery(hardwareEventsPageQuery(id))
  const [filter, setFilter] = useState<EventsFilter>(EMPTY_FILTER)
  const [start, setStart] = useState(0)
  const [length, setLength] = useState(10)
  const [order, setOrder] = useState<{ column: number; dir: 'asc' | 'desc' }>({ column: 0, dir: 'desc' })

  const table = useQuery({
    ...hardwareEventsTableQuery(id, {
      start,
      length,
      orderColumn: order.column,
      orderDir: order.dir,
      search: filter.q,
      category: filter.category as HardwareCategory,
      from: filter.from,
      to: filter.to,
    }),
    placeholderData: keepPreviousData,
    enabled: !!page.data,
  })

  if (page.error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {page.error.message}
      </div>
    )
  }
  if (!page.data) return <Spinner />

  const apply = (next: EventsFilter) => {
    setFilter(next)
    setStart(0)
  }

  return (
    <div className="device-hardware-events">
      <EventsChrome
        id="odEventsInventoree"
        table={`#${TABLE}`}
        categories={page.data.categories}
        startedOn={page.data.active}
        filter={filter}
        counts={table.data?.counts ?? { all: 0, critical: 0, major: 0, minor: 0 }}
        onChange={apply}
      />
      <div className="row">
        <div className="col-md-12">
          <DetailsTable
            deviceId={id}
            rows={table.data?.data ?? []}
            total={table.data?.recordsFiltered ?? 0}
            fetching={table.isFetching}
            error={table.isError ? (table.error as Error).message : null}
            start={start}
            length={length}
            order={order}
            onStart={setStart}
            onLength={(n) => {
              setLength(n)
              setStart(0)
            }}
            onOrder={(next) => {
              setOrder(next)
              setStart(0)
            }}
          />
        </div>
      </div>
    </div>
  )
}

const COLUMNS: { key: keyof HardwareEventRow; title: string; titleHtml?: string; orderable: boolean; width: number }[] = [
  { key: 'date', title: 'Date', titleHtml: '<i class="far fa-calendar"></i> Date', orderable: true, width: 150 },
  { key: 'message', title: 'Message', orderable: true, width: 150 },
  { key: 'user', title: 'User', orderable: true, width: 150 },
  { key: 'authorised', title: 'Authorised', orderable: false, width: 90 },
]

function DetailsTable({
  deviceId,
  rows,
  total,
  fetching,
  error,
  start,
  length,
  order,
  onStart,
  onLength,
  onOrder,
}: {
  deviceId: number
  rows: HardwareEventRow[]
  total: number
  fetching: boolean
  error: string | null
  start: number
  length: number
  order: { column: number; dir: 'asc' | 'desc' }
  onStart(n: number): void
  onLength(n: number): void
  onOrder(next: { column: number; dir: 'asc' | 'desc' }): void
}) {
  const [openInitial, setOpenInitial] = useState<string | null>(null)
  const initial = useQuery({
    ...initialEventsQuery(deviceId),
    enabled: openInitial !== null,
  })
  const pages = Math.max(1, Math.ceil(total / length) || 1)
  const page = Math.floor(start / length)
  const from = total === 0 ? 0 : start + 1
  const to = Math.min(start + length, total)

  return (
    <div className="dataTables_wrapper form-inline dt-bootstrap no-footer" id={`${TABLE}_wrapper`}>
      <div className="row">
        <div className="col-sm-12 multiActions"></div>
      </div>
      <div className="row">
        <div className="col-sm-6 dataTableF"></div>
        <div className="col-sm-6 dataTableA"></div>
      </div>
      <div className="row">
        <div className="col-sm-12 userFilters"></div>
      </div>
      <div className="row">
        <div className="col-sm-12">
          <div className="dataTables_processing" id={`${TABLE}_processing`} style={{ display: fetching ? 'block' : 'none' }}>
            Processing...
          </div>
          <table className="table table-striped table-hover dataTable events-table no-footer" width="100%" id={TABLE} role="grid">
            <thead>
              <tr role="row">
                {COLUMNS.map((col, i) => {
                  const sorted = order.column === i
                  const cls = [
                    'td-small a-left',
                    col.orderable ? (sorted ? (order.dir === 'asc' ? 'sorting_asc' : 'sorting_desc') : 'sorting') : 'sorting_disabled',
                  ].join(' ')
                  return (
                    <th
                      key={col.key}
                      className={cls}
                      style={{ width: col.width }}
                      aria-label={`${col.title}: activate to sort column ${sorted && order.dir === 'asc' ? 'descending' : 'ascending'}`}
                      aria-sort={sorted ? (order.dir === 'asc' ? 'ascending' : 'descending') : undefined}
                      onClick={() => {
                        if (!col.orderable) return
                        onOrder({ column: i, dir: sorted && order.dir === 'desc' ? 'asc' : 'desc' })
                      }}
                    >
                      {col.titleHtml ? <span dangerouslySetInnerHTML={{ __html: col.titleHtml }} /> : col.title}
                    </th>
                  )
                })}
              </tr>
            </thead>
            <tbody>
              {error ? (
                <tr className="odd">
                  <td colSpan={4} className="dataTables_empty">
                    Something went wrong: {error}
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr className="odd">
                  <td colSpan={4} className="dataTables_empty">
                    No data available in table
                  </td>
                </tr>
              ) : (
                rows.map((row, i) => {
                  const initialRow = row.kind === 'initial' || row.DT_RowId.startsWith('row_initial_')
                  return (
                    <Fragment key={row.DT_RowId}>
                      <tr
                        id={row.DT_RowId}
                        role="row"
                        className={i % 2 === 0 ? 'odd' : 'even'}
                        onClick={(e) => {
                          if (!initialRow) return
                          if (!(e.target as HTMLElement).closest('a.ev-initial')) return
                          e.preventDefault()
                          setOpenInitial((cur) => (cur === row.DT_RowId ? null : row.DT_RowId))
                        }}
                      >
                        {COLUMNS.map((col) => (
                          <td key={col.key} className="td-small a-left" dangerouslySetInnerHTML={{ __html: row[col.key] }} />
                        ))}
                      </tr>
                      {initialRow && openInitial === row.DT_RowId ? (
                        <tr className="rowDetails">
                          <td className="rowDetails" colSpan={4}>
                            {initial.isPending ? (
                              <div className="ev-initial-groups">Loading…</div>
                            ) : (
                              <div
                                dangerouslySetInnerHTML={{
                                  __html: groupInitialEvents(initial.data?.events ?? []),
                                }}
                              />
                            )}
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
      <div className="row">
        <div className="col-sm-2">
          <div className="dataTables_length custom_length_class" id={`${TABLE}_length`}>
            <label>
              Show{' '}
              <select
                name={`${TABLE}_length`}
                aria-controls={TABLE}
                className="form-control input-sm"
                value={length}
                onChange={(e) => onLength(Number(e.target.value))}
              >
                {LENGTHS.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>{' '}
              entries
            </label>
          </div>
        </div>
        <div className="col-sm-4 aright">
          <div className="dataTables_info" id={`${TABLE}_info`} role="status" aria-live="polite">
            Showing {from} to {to} of {total} entries
          </div>
        </div>
        <div className="col-sm-6">
          <div className="dataTables_paginate paging_simple_numbers" id={`${TABLE}_paginate`}>
            <ul className="pagination">
              <li className={`paginate_button previous${page <= 0 ? ' disabled' : ''}`} id={`${TABLE}_previous`}>
                <a href="#" onClick={(e) => { e.preventDefault(); if (page > 0) onStart((page - 1) * length) }}>
                  Previous
                </a>
              </li>
              {pageButtons(page, pages).map((b, i) =>
                b === 'ellipsis' ? (
                  <li key={`e${i}`} className="paginate_button disabled">
                    <a href="#" onClick={(e) => e.preventDefault()}>
                      …
                    </a>
                  </li>
                ) : (
                  <li key={b} className={`paginate_button${b === page ? ' active' : ''}`}>
                    <a href="#" onClick={(e) => { e.preventDefault(); onStart(b * length) }}>
                      {b + 1}
                    </a>
                  </li>
                ),
              )}
              <li className={`paginate_button next${page >= pages - 1 ? ' disabled' : ''}`} id={`${TABLE}_next`}>
                <a href="#" onClick={(e) => { e.preventDefault(); if (page < pages - 1) onStart((page + 1) * length) }}>
                  Next
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div className="clear"></div>
    </div>
  )
}
